import React, { useState, useEffect } from 'react';
import './ActionsTracker.css';
import { useActions } from '../contexts/ActionsContext';
import DatePickerCell from './DatePickerCell';

const ActionsTracker = () => {
  const { actions, teamMembers, toggleActionComplete, assignAction, updateActionStatus, updateActionDueDate, updateActionPriority, deleteAction } = useActions();
  const [expandedSections, setExpandedSections] = useState({
    'Top Priorities': true,
    'Low but Important': true,
    'Recently Assigned': false,
    'In Progress': true
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterTab, setFilterTab] = useState('Open');
  const [animatingActions, setAnimatingActions] = useState({});
  const [delayedActions, setDelayedActions] = useState({});
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [openPriorityDropdown, setOpenPriorityDropdown] = useState(null);

  // Professional status options
  const statusOptions = ['Not Started', 'In Progress', 'On Hold', 'Complete', 'Cancelled'];
  const priorityOptions = ['Critical', 'High', 'Medium', 'Low'];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.status-dropdown-container')) {
        setOpenStatusDropdown(null);
      }
      if (!event.target.closest('.priority-dropdown-container')) {
        setOpenPriorityDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Filter actions based on tab
  const filteredActions = React.useMemo(() => {
    const filtered = actions.filter(action => {
      // If action is animating, show it regardless of filter
      if (animatingActions[action.id]) {
        return true;
      }
      
      // Only apply delayed hiding for Open/Closed tabs, not All
      if (delayedActions[action.id] && filterTab !== 'All') {
        return false;
      }
      
      switch (filterTab) {
        case 'Open':
          return action.status !== 'Complete' && action.status !== 'Cancelled';
        case 'Closed':
          return action.status === 'Complete' || action.status === 'Cancelled';
        case 'All':
        default:
          return true;
      }
    });
    
    return filtered;
  }, [actions, filterTab, animatingActions, delayedActions]);

  // Sort actions
  const sortedActions = React.useMemo(() => {
    if (!sortConfig.key) return filteredActions;
    
    return [...filteredActions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'assignee') {
        aValue = teamMembers.find(m => m.id === aValue)?.name || 'N/A';
        bValue = teamMembers.find(m => m.id === bValue)?.name || 'N/A';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredActions, sortConfig, teamMembers]);

  // Group sorted actions by section
  const groupedActions = sortedActions.reduce((acc, action) => {
    const section = action.section || 'Top Priorities';
    if (!acc[section]) acc[section] = [];
    acc[section].push(action);
    return acc;
  }, {});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusChange = (actionId, newStatus) => {
    updateActionStatus(actionId, newStatus);
    setOpenStatusDropdown(null);
  };

  const handlePriorityChange = (actionId, newPriority) => {
    updateActionPriority(actionId, newPriority);
    setOpenPriorityDropdown(null);
  };

  const handleDeleteAction = (actionId) => {
    // Play the sad sound when deleting
    playMinorChord();
    deleteAction(actionId);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return '#dc3545';
      case 'High': return '#fd7e14';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const playMagicSparkle = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [800, 1000, 1200, 1600, 2000];
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.05);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + i * 0.05 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.05 + 0.2);
      osc.start(ctx.currentTime + i * 0.05);
      osc.stop(ctx.currentTime + i * 0.05 + 0.3);
    });
  };

  const playMinorChord = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [261.63, 311.13, 392]; // C minor chord
    
    notes.forEach(freq => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start();
      osc.stop(ctx.currentTime + 0.5);
    });
  };

  const handleActionComplete = (actionId) => {
    // Find the action to check its current state
    const action = actions.find(a => a.id === actionId);
    const isCompleting = action && !action.completed;
    
    // Add animation class first
    setAnimatingActions(prev => ({ ...prev, [actionId]: true }));
    
    // Toggle the action immediately
    toggleActionComplete(actionId);
    
    // Play appropriate sound based on whether we're completing or reopening
    if (isCompleting) {
      playMagicSparkle();
    } else {
      playMinorChord();
    }
    
    // After animation completes, hide from current view
    setTimeout(() => {
      // Mark as delayed (will hide from current filter)
      setDelayedActions(prev => ({ ...prev, [actionId]: true }));
      
      // Remove animation class
      setAnimatingActions(prev => {
        const newState = { ...prev };
        delete newState[actionId];
        return newState;
      });
      
      // Clean up delayed state after a brief moment
      setTimeout(() => {
        setDelayedActions(prev => {
          const newState = { ...prev };
          delete newState[actionId];
          return newState;
        });
      }, 100);
    }, 800);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Not Started': return '#6c757d';
      case 'In Progress': return '#007bff';
      case 'On Hold': return '#dc3545';
      case 'Complete': return '#28a745';
      case 'Cancelled': return '#868e96';
      default: return '#6c757d';
    }
  };

  const formatDueDate = (date) => {
    if (!date) return 'No due date';
    const dueDate = new Date(date);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  return (
    <div className="actions-tracker">
      <div className="tracker-header">
        <h2>My Tasks</h2>
      </div>

      <div className="tracker-toolbar">
        <div className="toolbar-left">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${filterTab === 'All' ? 'active' : ''}`}
              onClick={() => setFilterTab('All')}
            >
              All
            </button>
            <button 
              className={`filter-tab ${filterTab === 'Open' ? 'active' : ''}`}
              onClick={() => setFilterTab('Open')}
            >
              Open
            </button>
            <button 
              className={`filter-tab ${filterTab === 'Closed' ? 'active' : ''}`}
              onClick={() => setFilterTab('Closed')}
            >
              Closed
            </button>
          </div>
        </div>
        <button className="add-task-btn">
          <span>+ Add task</span>
        </button>
      </div>

      <div className="tracker-columns">
        <div className="column-headers">
          <div className="column-name sortable" onClick={() => handleSort('text')}>
            Name
            <span className="sort-icon">{sortConfig.key === 'text' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
          <div className="column-due sortable" onClick={() => handleSort('dueDate')}>
            Due date
            <span className="sort-icon">{sortConfig.key === 'dueDate' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
          <div className="column-project sortable" onClick={() => handleSort('accountName')}>
            Projects
            <span className="sort-icon">{sortConfig.key === 'accountName' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
          <div className="column-assignee sortable" onClick={() => handleSort('assignee')}>
            Collaborator
            <span className="sort-icon">{sortConfig.key === 'assignee' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
          <div className="column-overall sortable" onClick={() => handleSort('status')}>
            Overall Status
            <span className="sort-icon">{sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
          <div className="column-priority sortable" onClick={() => handleSort('priority')}>
            Priority
            <span className="sort-icon">{sortConfig.key === 'priority' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
          </div>
        </div>

        <div className="actions-sections">
          {Object.entries(groupedActions).map(([section, sectionActions]) => (
            <div key={section} className="action-section">
              <div 
                className="section-header"
                onClick={() => toggleSection(section)}
              >
                <span className="section-toggle">
                  {expandedSections[section] ? '▼' : '▶'}
                </span>
                <span className="section-title">{section}</span>
              </div>

              {expandedSections[section] && (
                <div className="section-actions">
                  {sectionActions.map(action => (
                    <div 
                      key={action.id} 
                      className={`action-row ${animatingActions[action.id] ? 'completing' : ''}`}
                    >
                      <div className="column-name">
                        <input
                          type="checkbox"
                          checked={action.completed}
                          onChange={() => handleActionComplete(action.id)}
                          className="action-checkbox"
                        />
                        <span className={`action-text ${action.completed ? 'completed' : ''}`}>
                          {action.text}
                        </span>
                      </div>
                      
                      <div className="column-due">
                        <DatePickerCell 
                          date={action.dueDate}
                          onDateChange={(newDate) => updateActionDueDate(action.id, newDate)}
                          formatDate={formatDueDate}
                        />
                      </div>
                      
                      <div className="column-project">
                        <span className="project-name">{action.accountName}</span>
                      </div>
                      
                      <div className="column-assignee">
                        <select 
                          className="assignee-select"
                          value={action.assignee || ''}
                          onChange={(e) => assignAction(action.id, e.target.value)}
                        >
                          <option value="">N/A</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="column-overall">
                        <div className="status-dropdown-container">
                          <button 
                            className="status-button"
                            onClick={() => setOpenStatusDropdown(openStatusDropdown === action.id ? null : action.id)}
                            style={{
                              backgroundColor: getStatusColor(action.status) + '20',
                              color: getStatusColor(action.status),
                              border: `1px solid ${getStatusColor(action.status)}40`
                            }}
                          >
                            {action.status}
                            <span className="dropdown-arrow">▼</span>
                          </button>
                          {openStatusDropdown === action.id && (
                            <div className="dropdown-menu status-menu">
                              {statusOptions.map(status => (
                                <div
                                  key={status}
                                  className="dropdown-item status-item"
                                  onClick={() => handleStatusChange(action.id, status)}
                                  style={{
                                    backgroundColor: getStatusColor(status) + '20',
                                    color: getStatusColor(status),
                                    border: `1px solid ${getStatusColor(status)}40`
                                  }}
                                >
                                  {status}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="column-priority">
                        <div className="priority-dropdown-container">
                          <button 
                            className="priority-badge"
                            onClick={() => setOpenPriorityDropdown(openPriorityDropdown === action.id ? null : action.id)}
                            style={{ 
                              backgroundColor: `${getPriorityColor(action.priority)}20`,
                              color: getPriorityColor(action.priority),
                              border: `1px solid ${getPriorityColor(action.priority)}40`
                            }}
                          >
                            {action.priority}
                            <span className="dropdown-arrow">▼</span>
                          </button>
                          {openPriorityDropdown === action.id && (
                            <div className="dropdown-menu priority-menu">
                              {priorityOptions.map(priority => (
                                <div
                                  key={priority}
                                  className="dropdown-item priority-item"
                                  onClick={() => handlePriorityChange(action.id, priority)}
                                  style={{
                                    backgroundColor: getPriorityColor(priority) + '20',
                                    color: getPriorityColor(priority),
                                    border: `1px solid ${getPriorityColor(priority)}40`
                                  }}
                                >
                                  {priority}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="column-delete">
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteAction(action.id)}
                          title="Delete action"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <button className="add-task-section-btn">
                    Add task...
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActionsTracker;